import { useCallback, useSyncExternalStore } from 'react';
import { useSessionOrNull } from '~/contexts/SessionContext';
import type { PendingChangesState } from '~/domain/changes/PendingChangesStore';

export type PendingChanges = {
    /** Null when no patient session exists. */
    readonly state: PendingChangesState | null;
    /** Whether this session can save to the connector database at all. */
    readonly canSave: boolean;
};

/** Total pending entries across every dataset kind. */
export function pendingEntryCount(state: PendingChangesState | null): number {
    if (!state) {
        return 0;
    }
    return (
        state.byStructureSet.size +
        state.byRegistration.size +
        state.byImage.size +
        state.byDose.size
    );
}

const NO_SUBSCRIPTION = (): (() => void) => () => {};

/** Live view over the session's {@link PendingChangesStore}. */
export function usePendingChanges(): PendingChanges {
    const session = useSessionOrNull();
    const store = session?.pendingChangesStore ?? null;

    const subscribe = useCallback(
        (listener: () => void) =>
            store ? store.subscribe(listener) : NO_SUBSCRIPTION(),
        [store]
    );
    const getSnapshot = useCallback(
        () => (store ? store.getState() : null),
        [store]
    );
    const state = useSyncExternalStore(subscribe, getSnapshot);

    return {
        state,
        canSave: session?.patientSaveService.canSave ?? false,
    };
}
